import { useState } from "react"
import type { NostrEvent } from "@/nostr/types"
import { useAuth } from "@/state/AuthContext"
import { useToast } from "@/state/ToastContext"
import { usePublish } from "@/hooks/usePublish"
import { useBookmarks } from "@/state/BookmarkContext"
import type { EngagementResult } from "@/hooks/useInteractions"
import { formatCount } from "@/utils/format"
import { noteIdOf } from "@/utils/nip19"
import { Icon } from "./Icon"
import { ReplyDialog } from "./ReplyDialog"

export interface NoteActionsProps {
  event: NostrEvent
  engagement?: EngagementResult
  showBookmark?: boolean
  replies?: number
  /** Renders nothing (used for previews without actions). */
  hidden?: boolean
  /** Called with the signed event after a successful reaction/repost. */
  onReacted?: (event: NostrEvent) => void
  onReposted?: (event: NostrEvent) => void
  /** Called after a reply was published through the reply dialog. */
  onReplied?: () => void
}

/** Reply / react / repost / bookmark / copy actions for one note. */
export function NoteActions({
  event,
  engagement,
  showBookmark = false,
  replies,
  hidden = false,
  onReacted,
  onReposted,
  onReplied,
}: NoteActionsProps) {
  const { canSign } = useAuth()
  const { push } = useToast()
  const { react, repost } = usePublish()
  const bookmarks = useBookmarks()
  const [pending, setPending] = useState<"react" | "repost" | "bookmark">()
  const [replyOpen, setReplyOpen] = useState(false)

  if (hidden) return null

  const reacted = Boolean(engagement?.myReaction)
  const reposted = Boolean(engagement?.myRepost)
  const bookmarked = showBookmark ? bookmarks.isBookmarked(event.id) : false
  const replyCount = replies ?? engagement?.replies

  const onReact = async () => {
    if (!canSign) {
      push("info", "Log in to react")
      return
    }
    setPending("react")
    try {
      const outcome = await react(event)
      if (outcome.ok && outcome.summary?.event) onReacted?.(outcome.summary.event)
    } finally {
      setPending(undefined)
    }
  }

  const onRepost = async () => {
    if (!canSign) {
      push("info", "Log in to repost")
      return
    }
    setPending("repost")
    try {
      const outcome = await repost(event)
      if (outcome.ok && outcome.summary?.event) onReposted?.(outcome.summary.event)
    } finally {
      setPending(undefined)
    }
  }

  const onBookmark = async () => {
    if (!canSign) {
      push("info", "Log in to bookmark notes")
      return
    }
    setPending("bookmark")
    try {
      await bookmarks.toggleBookmark(event.id)
    } finally {
      setPending(undefined)
    }
  }

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/n/${noteIdOf(event.id)}`)
      push("success", "Link copied")
    } catch {
      push("error", "Could not copy the link")
    }
  }

  return (
    <>
      <div
        className="note-actions"
        onClick={(clickEvent) => clickEvent.stopPropagation()}
        onKeyDown={(keyEvent) => keyEvent.stopPropagation()}
        role="group"
        aria-label="Note actions"
      >
        <button
          type="button"
          className="note-action"
          title="Reply"
          aria-haspopup="dialog"
          onClick={() => setReplyOpen(true)}
        >
          <Icon name="reply" size={16} />
          {typeof replyCount === "number" && replyCount > 0 && <span>{formatCount(replyCount)}</span>}
        </button>
        <button
          type="button"
          className={`note-action ${reacted ? "active" : ""}`}
          onClick={onReact}
          disabled={pending === "react"}
          aria-pressed={reacted}
          title={reacted ? "You reacted" : "React"}
        >
          <Icon name="heart" size={16} />
          {engagement && engagement.likes > 0 && <span>{formatCount(engagement.likes)}</span>}
        </button>
        <button
          type="button"
          className={`note-action ${reposted ? "active" : ""}`}
          onClick={onRepost}
          disabled={pending === "repost"}
          aria-pressed={reposted}
          title="Repost"
        >
          <Icon name="repeat" size={16} />
          {engagement && engagement.reposts > 0 && <span>{formatCount(engagement.reposts)}</span>}
        </button>
        {showBookmark && (
          <button
            type="button"
            className={`note-action ${bookmarked ? "active" : ""}`}
            onClick={onBookmark}
            disabled={pending === "bookmark"}
            aria-pressed={bookmarked}
            title={bookmarked ? "Remove bookmark" : "Bookmark"}
          >
            <Icon name="bookmark" size={16} />
          </button>
        )}
        <button type="button" className="note-action" onClick={onCopy} title="Copy link">
          <Icon name="copy" size={15} />
        </button>
      </div>

      <ReplyDialog
        open={replyOpen}
        onClose={() => setReplyOpen(false)}
        replyTo={event}
        onReplied={onReplied}
      />
    </>
  )
}
